import { ObjectUpdatedType } from '@/enums/ObjectUpdatedType.enum';
import { UserHelper } from '@/helpers/user.helper';
import { ActionService } from '@/services/action.service';
import { AppConfigService } from '@/services/app-config.service';
import { NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PropertiesService, PropertyAccessTypeFilter, RealProperty, SortDirection } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { DataLoadingComponent } from '../data-loading/data-loading.component';
import { PropertyCardComponent } from '../property-card/property-card.component';
import { RoundedLinkComponent } from '../rounded-link/rounded-link.component';

@Component({
  selector: 'app-property-card-list',
  templateUrl: './property-card-list.component.html',
  styleUrl: './property-card-list.component.scss',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    DataLoadingComponent,
    PropertyCardComponent,
    RoundedLinkComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ]
})
export class PropertyCardListComponent implements OnInit, OnDestroy {
  readonly PAGING_ITEMS = 12;

  favoriteRealProperties: RealProperty[] = [];
  myRealProperties: RealProperty[] = [];
  sharedRealProperties: RealProperty[] = [];

  isFavoriteOwned = false;

  loadingFavorite = false;
  loadingMy = false;
  loadingShared = false;

  loadingMoreFavorite = false;
  loadingMoreMy = false;
  loadingMoreShared = false;

  hasMoreFavorite = false;
  hasMoreMy = false;
  hasMoreShared = false;

  countMoreFavorite = 0;
  countMoreMy = 0;
  countMoreShared = 0;

  subs: Subscription[] = [];

  currentUserUid: string;

  canAddProperty = false;
  userReachedPropertyLimit = false;

  constructor(
    private propertiesService: PropertiesService,
    private actionService: ActionService,
    private userHelper: UserHelper,
    private appConfigService: AppConfigService
  ) {
    this.canAddProperty = userHelper.canAddProperty();
    this.currentUserUid = this.userHelper.currentUser.userId;
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.favoriteRealProperties = [];
    this.myRealProperties = [];
    this.sharedRealProperties = [];
    this.loadFavorite(false);
    this.loadMy(false);
    this.loadShared(false);
    this.subs.push(
      this.appConfigService.objectUpdated.subscribe((type) => {
        if (type === ObjectUpdatedType.PROPERTY) {
          this.loadData();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  loadFavorite(more: boolean) {
    if (more) {
      this.loadingMoreFavorite = true;
    } else {
      this.loadingFavorite = true;
    }
    const offset = this.favoriteRealProperties.length;

    this.subs.push(
      this.propertiesService
        .getRealPropertyList(
          PropertyAccessTypeFilter.FAVORITE,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          'dateCreate',
          SortDirection.ASC,
          offset,
          this.PAGING_ITEMS
        )
        .subscribe({
          next: (d) => {
            this.favoriteRealProperties = this.favoriteRealProperties.concat(d.list);
            this.hasMoreFavorite = this.favoriteRealProperties.length < d.paging.totalItems;
            this.countMoreFavorite = d.paging.totalItems - this.favoriteRealProperties.length;
            this.loadingFavorite = false;
            this.loadingMoreFavorite = false;
            d.list.forEach((prop) => {
              if (!this.isFavoriteOwned) {
                this.isFavoriteOwned = this.userHelper.isPropertyOwner(prop);
              }
            });
          },
          error: () => {
            this.loadingFavorite = false;
            this.loadingMoreFavorite = false;
          }
        })
    );
  }

  loadMy(more: boolean) {
    if (more) {
      this.loadingMoreMy = true;
    } else {
      this.loadingMy = true;
    }
    const offset = this.myRealProperties.length;

    this.subs.push(
      this.propertiesService
        .getRealPropertyList(
          PropertyAccessTypeFilter.MINE_NO_FAVORITE,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          'dateCreate',
          SortDirection.ASC,
          offset,
          this.PAGING_ITEMS
        )
        .subscribe({
          next: (d) => {
            this.myRealProperties = this.myRealProperties.concat(d.list);
            this.hasMoreMy = this.myRealProperties.length < d.paging.totalItems;
            this.countMoreMy = d.paging.totalItems - this.myRealProperties.length;
            this.loadingMy = false;
            this.loadingMoreMy = false;
          },
          error: () => {
            this.loadingMy = false;
            this.loadingMoreMy = false;
          }
        })
    );
  }

  loadShared(more: boolean) {
    if (more) {
      this.loadingMoreShared = true;
    } else {
      this.loadingShared = true;
    }
    const offset = this.sharedRealProperties.length;

    this.subs.push(
      this.propertiesService
        .getRealPropertyList(
          PropertyAccessTypeFilter.SHARED_NO_FAVORITE,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          'dateCreate',
          SortDirection.ASC,
          offset,
          this.PAGING_ITEMS
        )
        .subscribe({
          next: (d) => {
            this.sharedRealProperties = this.sharedRealProperties.concat(d.list);
            this.hasMoreShared = this.sharedRealProperties.length < d.paging.totalItems;
            this.countMoreShared = d.paging.totalItems - this.sharedRealProperties.length;
            this.loadingShared = false;
            this.loadingMoreShared = false;
          },
          error: () => {
            this.loadingShared = false;
            this.loadingMoreShared = false;
          }
        })
    );
  }

  add() {
    this.actionService.addProperty();
  }
}
